import { Op, OpType, SyncTable } from "../sync/ws-envelope";

/**
 * Pure decision layer for the durable op-log flush (peer-sync Phase 3, native
 * push half). The stateful drain lives in NativeSyncService; this module holds
 * the two bits worth isolating: reconstructing a stored `sync_oplog` row back
 * into a wire `Op`, and classifying a server REJECT reason as terminal (drop
 * the outbox row) vs transient (retain + retry).
 */

/** A row read from `sync_oplog` (the `acked = 0` outbox), oldest-first. */
export interface OplogRow {
  localSeq: number;
  tableName: string;
  rowId: string;
  /** OpType as stored (validated on reconstruct). */
  op: string;
  /** JSON.stringify(op.row) at append time. */
  rowJson: string;
}

const OP_TYPES: ReadonlySet<string> = new Set<OpType>(["upsert", "obsolete", "delete"]);

/**
 * Rebuild the wire `Op` from a stored op-log row. Returns null for a row that
 * can never produce a valid op — unparseable `rowJson`, a non-object payload,
 * an unknown op type, or an empty id/table. A corrupt row must not abort the
 * whole flush; the orchestrator marks such rows acked (dropped) so they don't
 * wedge the outbox head forever.
 */
export function reconstructWireOp(r: OplogRow): Op | null {
  if (!OP_TYPES.has(r.op)) return null;
  if (typeof r.rowId !== "string" || r.rowId.length === 0) return null;
  if (typeof r.tableName !== "string" || r.tableName.length === 0) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(r.rowJson);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  return {
    rowId: r.rowId,
    table: r.tableName as SyncTable,
    type: r.op as OpType,
    row: parsed as Record<string, unknown>,
  };
}

/**
 * The server refuses a frame above 8 MiB. `rowJson.length` counts UTF-16 units
 * and one unit is at most 3 bytes in UTF-8. So 2 M chars is at most 6 MB, which
 * leaves room for the envelope.
 */
export const FLUSH_MAX_CHARS = 2_000_000;

/** The reconstructed wire ops to APPLY, plus the localSeqs they map to. */
export interface FlushBatch {
  /** Ops to send, in localSeq order, capped at maxBatch. */
  ops: Op[];
  /** localSeqs of the rows in `ops`, parallel to send order (for in-flight tracking). */
  sentSeqs: number[];
  /** localSeqs of rows that could not be reconstructed — drop (mark acked), never send. */
  corruptSeqs: number[];
}

/**
 * Build the next APPLY batch from pending op-log rows (already filtered to
 * not-in-flight, ordered by localSeq). Reconstructs each row, caps the sendable
 * set at `maxBatch` ops and `maxChars` of row text, and segregates corrupt
 * rows. The first sendable op always goes in, whatever its size. Corrupt rows
 * do not count against either cap — they carry no wire cost.
 */
export function planFlushBatch(rows: readonly OplogRow[], maxBatch: number, maxChars: number): FlushBatch {
  const ops: Op[] = [];
  const sentSeqs: number[] = [];
  const corruptSeqs: number[] = [];
  let chars = 0;
  for (const r of rows) {
    if (ops.length >= maxBatch) break;
    const op = reconstructWireOp(r);
    if (op === null) {
      corruptSeqs.push(r.localSeq);
      continue;
    }
    if (ops.length > 0 && chars + r.rowJson.length > maxChars) break;
    chars += r.rowJson.length;
    ops.push(op);
    sentSeqs.push(r.localSeq);
  }
  return { ops, sentSeqs, corruptSeqs };
}

/**
 * Reasons (mirroring efb-app-server `src/sync/ops.ts`) for which re-sending the
 * identical op will ALWAYS reproduce the same reject — so the outbox row is
 * dropped (marked acked) rather than retried:
 *   - `lww`            — server holds a strictly-newer / equal-ms-conflicting
 *                        version; ours is superseded (server broadcasts the
 *                        winner via REMOTE).
 *   - `not-found`      — tenant-isolation miss, or obsolete/delete of a row the
 *                        server never saw; a retry can't conjure it.
 *   - `…immutable`     — the write would have changed a domain-immutable column
 *                        on an existing row (a recorded attestation); resending
 *                        it unchanged would be refused identically.
 *   - validation       — `unknown table`, `does not support`, `does not match`,
 *                        `updatedAt missing`: a malformed op (client bug).
 * Anything else (a whole-txn failure, an unexpected better-sqlite3 exception
 * message) is treated as TRANSIENT and retained, so a momentary hiccup never
 * silently drops a pending edit.
 */
const TERMINAL_REJECT_MATCHERS: ReadonlyArray<(r: string) => boolean> = [
  (r) => r === "lww",
  (r) => r === "not-found",
  (r) => r.includes("immutable"),
  (r) => r.startsWith("unknown table"),
  (r) => r.includes("does not support"),
  (r) => r.includes("does not match"),
  (r) => r.includes("updatedAt missing"),
];

export function isTerminalReject(reason: string): boolean {
  if (typeof reason !== "string") return false;
  return TERMINAL_REJECT_MATCHERS.some((m) => m(reason));
}

/** Backoff base / ceiling for the transient-reject retry tick (ms). */
export const RETRY_BASE_MS = 3_000;
export const RETRY_CAP_MS = 60_000;

/**
 * Exponential backoff for the transient-reject retry tick: `base · 2^attempt`,
 * clamped to `cap`. A transient REJECT (or a >batch tail deferred behind one)
 * leaves the op `acked=0` with no self-retrigger — this delay paces the re-flush
 * so a persistent transient error doesn't hot-loop. `attempt` resets to 0 on any
 * ACK (progress), so a one-off hiccup retries quickly. Defensive on a non-finite
 * / negative attempt (treated as 0). A huge attempt overflows `2^attempt` to
 * Infinity → clamps to `cap`, never NaN.
 */
export function nextRetryDelayMs(attempt: number, baseMs = RETRY_BASE_MS, capMs = RETRY_CAP_MS): number {
  const a = Number.isFinite(attempt) && attempt > 0 ? Math.floor(attempt) : 0;
  return Math.min(capMs, baseMs * Math.pow(2, a));
}
